import { lazy, useMemo } from 'react';
import { Box, Text, useMantineTheme } from '@mantine/core';
import { IconMapPin } from '@tabler/icons-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { WidgetProps, TaskData } from '../types';
import { registerWidget } from '../registry';
import { FadeIn } from '../../../animations';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// TaskByLocationGraph Widget Component
// ============================================================================

function TaskByLocationGraphWidget({ globalData, isCollapsed }: WidgetProps) {
  const theme = useMantineTheme();
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  if (isCollapsed) return null;

  const tasks = globalData?.tasks || [];

  // Group tasks by location
  const locationData = useMemo(() => {
    const locationMap = new Map<string, { total: number; completed: number }>();

    tasks.forEach((task) => {
      const location = task.location || 'Unassigned';
      const existing = locationMap.get(location) || { total: 0, completed: 0 };
      existing.total += 1;
      if (task.status === 'done') {
        existing.completed += 1;
      }
      locationMap.set(location, existing);
    });

    return Array.from(locationMap.entries())
      .map(([name, data]) => ({
        name: name.length > 12 ? `${name.slice(0, 12)}...` : name,
        fullName: name,
        total: data.total,
        completed: data.completed,
        pending: data.total - data.completed,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);
  }, [tasks]);

  const colors = [
    theme.colors.blue[5],
    theme.colors.cyan[5],
    theme.colors.teal[5],
    theme.colors.green[5],
    theme.colors.lime[5],
    theme.colors.yellow[5],
  ];

  if (locationData.length === 0) {
    return (
      <FadeIn style={{ height: '100%' }}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: theme.colors.gray[5],
          }}
        >
          <IconMapPin size={32} stroke={1.5} />
          <Text size="sm" mt="xs" style={accessibilityStyles}>
            No location data available
          </Text>
        </Box>
      </FadeIn>
    );
  }

  return (
    <FadeIn style={{ height: '100%' }}>
      <Box style={{ height: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={locationData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.gray[2]} />
            <XAxis type="number" stroke={theme.colors.gray[5]} fontSize={12} />
            <YAxis
              dataKey="name"
              type="category"
              stroke={theme.colors.gray[5]}
              fontSize={11}
              width={80}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.white,
                border: `1px solid ${theme.colors.gray[2]}`,
                borderRadius: theme.radius.sm,
                fontSize: 12,
              }}
              // @ts-expect-error - Formatter type mismatch with recharts
              formatter={(value: number, name: string) => [
                value,
                name === 'completed' ? 'Completed' : 'Pending',
              ]}
              labelFormatter={(label) => {
                const item = locationData.find((d) => d.name === label);
                return item?.fullName || label;
              }}
            />
            <Bar dataKey="completed" stackId="a" fill={theme.colors.green[5]} name="completed">
              {locationData.map((entry, index) => (
                <Cell key={`cell-completed-${index}`} fill={theme.colors.green[5]} />
              ))}
            </Bar>
            <Bar dataKey="pending" stackId="a" fill={theme.colors.gray[3]} name="pending">
              {locationData.map((entry, index) => (
                <Cell key={`cell-pending-${index}`} fill={theme.colors.gray[3]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </FadeIn>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'task-by-location',
  name: 'Tasks by Location',
  description: 'Horizontal bar chart showing task distribution across locations',
  icon: IconMapPin,
  category: 'charts',
  component: lazy(() => Promise.resolve({ default: TaskByLocationGraphWidget })),
  sizes: {
    base: { cols: 12, rows: 5 },
    sm: { cols: 12, rows: 5 },
    md: { cols: 6, rows: 5 },
    lg: { cols: 6, rows: 5 },
    xl: { cols: 4, rows: 5 },
    tv: { cols: 4, rows: 4 },
  },
  sizeVariants: {
    variants: ['small', 'medium', 'large', 'full'],
    defaultVariant: 'medium',
    variantSizes: {
      small: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 6, rows: 4 },
        md: { cols: 4, rows: 4 },
        lg: { cols: 4, rows: 4 },
        xl: { cols: 3, rows: 4 },
        tv: { cols: 3, rows: 4 },
      },
      medium: {
        base: { cols: 12, rows: 5 },
        sm: { cols: 12, rows: 5 },
        md: { cols: 6, rows: 5 },
        lg: { cols: 6, rows: 5 },
        xl: { cols: 4, rows: 5 },
        tv: { cols: 4, rows: 4 },
      },
      large: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 8, rows: 6 },
        lg: { cols: 8, rows: 6 },
        xl: { cols: 6, rows: 6 },
        tv: { cols: 6, rows: 5 },
      },
      full: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 12, rows: 6 },
        xl: { cols: 12, rows: 6 },
        tv: { cols: 12, rows: 5 },
      },
    },
  },
  data: {
    sourceType: 'global',
    globalDataKeys: ['tasks'],
  },
  hasSettings: true,
});

export default TaskByLocationGraphWidget;
